import { useCallback, useEffect, useRef, useState } from 'react';

type PollingOptions<T> = {
  fetcher: () => Promise<T>;
  onSuccess: (data: T) => void;
  intervalMs: number;
  enabled?: boolean;
  immediate?: boolean;
  onError?: (error: unknown) => void;
  refreshKey?: unknown;
};

export function useVisibilityPolling<T>({
  fetcher,
  onSuccess,
  intervalMs,
  enabled = true,
  immediate = true,
  onError,
  refreshKey,
}: PollingOptions<T>) {
  const fetcherRef = useRef(fetcher);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const generationRef = useRef(0);
  const mountedRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const [isInitialLoading, setIsInitialLoading] = useState(enabled && immediate);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    fetcherRef.current = fetcher;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [fetcher, onError, onSuccess]);

  const refresh = useCallback((background = false) => {
    if (!enabled || document.hidden) return Promise.resolve();
    if (inFlightRef.current) return inFlightRef.current;

    if (!background && !hasLoadedRef.current) setIsInitialLoading(true);
    if (background) setIsRefreshing(true);

    const generation = generationRef.current;
    const request = fetcherRef.current()
      .then((data) => {
        if (!mountedRef.current || generation !== generationRef.current) return;
        hasLoadedRef.current = true;
        setError(null);
        onSuccessRef.current(data);
      })
      .catch((requestError: unknown) => {
        if (!mountedRef.current || generation !== generationRef.current) return;
        setError(requestError);
        onErrorRef.current?.(requestError);
      })
      .finally(() => {
        if (inFlightRef.current === request) inFlightRef.current = null;
        if (!mountedRef.current || generation !== generationRef.current) return;
        setIsInitialLoading(false);
        setIsRefreshing(false);
      });

    inFlightRef.current = request;
    return request;
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    generationRef.current += 1;
    inFlightRef.current = null;
    hasLoadedRef.current = false;
    if (!enabled) {
      return () => {
        mountedRef.current = false;
      };
    }

    const initialTimer = immediate
      ? window.setTimeout(() => void refresh(false), 0)
      : null;
    const interval = window.setInterval(() => {
      void refresh(true);
    }, intervalMs);
    const refreshWhenActive = () => {
      if (!document.hidden) void refresh(true);
    };
    document.addEventListener('visibilitychange', refreshWhenActive);
    window.addEventListener('focus', refreshWhenActive);

    return () => {
      mountedRef.current = false;
      if (initialTimer !== null) window.clearTimeout(initialTimer);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshWhenActive);
      window.removeEventListener('focus', refreshWhenActive);
    };
  }, [enabled, immediate, intervalMs, refresh, refreshKey]);

  return { error, isInitialLoading, isRefreshing, refresh };
}
